import { TextField } from "@mui/material"
import { IAdvertiser } from "advertiser/model"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { getObjectProperty, setValueAndGetObject } from "app/tools/common"
import { checkTouched, isTouched as hasBeenTouched } from "form/tools/validation"
import { Field, FieldProps } from "formik"
import { AddOrderPopupButton } from "order/components/AddOrderPopupButton"
import { IOrder } from "order/model"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { FieldAutoComplete } from "./base/FieldAutoComplete"

interface IFieldSelectOrderProps extends IFieldSelectOrderConnectProps {
  orderFieldName: string
  advertiserFieldName: string
  disabled?: boolean
  isDspDeal?: boolean
  error?: string
  sx?: any
  sxFirstField?: any
  sxSecondField?: any
  keepOrdersWithoutAdvertiser?: boolean
}

interface IFieldSelectOrderConnectProps {
  orders: IOrder[]
  advertisers: IAdvertiser[]
}

const FieldSelectOrderFn = React.memo(
  ({
    disabled,
    error,
    orderFieldName,
    orders,
    isDspDeal,
    advertiserFieldName,
    advertisers,
    sx,
    sxFirstField,
    sxSecondField,
    keepOrdersWithoutAdvertiser
  }: IFieldSelectOrderProps): JSX.Element => {
    if (!isDspDeal) {
      orders = orders.filter(o => o.advertiser_id !== null)
    }
    return (
      <Field name={orderFieldName}>
        {({ form: { touched, values, setFieldValue, setValues } }: FieldProps) => {
          const isTouched: boolean = hasBeenTouched(touched, orderFieldName)
          let orderId = getObjectProperty(values, orderFieldName)
          let advertiser: IAdvertiser | null = null
          if (!keepOrdersWithoutAdvertiser && !isDspDeal) {
            orders = orders.filter(o => typeof o.advertiser_id === "number")
          }
          if (!isDspDeal && orderId !== null) {
            const selectedOrder = orders.find(o => o.id === orderId)
            advertiser = selectedOrder ? advertisers.find(a => a.id === selectedOrder.advertiser_id) : null
            if (!advertiser) {
              orderId = null
            }
          }
          return (
            <FlexBox vertical ai="start" sx={{ ...sx }}>
              <FlexBox ai="start" wrap sx={{ ...sxFirstField }}>
                <FieldAutoComplete
                  id="order_id"
                  options={orders.map(o => o.id)}
                  optionLabelFn={value => {
                    const order = orders.find(o => o.id === value)
                    return order && order.name ? order.name : ""
                  }}
                  sx={{ width: "100%" }}
                  label={translate("label.order")}
                  value={orderId}
                  required
                  disabled={disabled}
                  error={isTouched ? error || "" : ""}
                  onChange={newOrderId => {
                    if (!isTouched) {
                      checkTouched(touched, orderFieldName)
                    }
                    const newOrder = orders.find(o => o.id === newOrderId)
                    let newValues = setValueAndGetObject({ ...values }, orderFieldName, newOrderId)
                    if (!isDspDeal) {
                      const newAdvertiser = advertisers.find(a => a.id === newOrder.advertiser_id)
                      if (!newAdvertiser) {
                        console.warn(`No advertiser found for order "${newOrder.name}"`)
                        return
                      }
                      newValues = setValueAndGetObject(newValues, advertiserFieldName, newAdvertiser.id)
                    }
                    setValues(newValues)
                  }}
                  onBlur={() => {
                    if (!isTouched) {
                      checkTouched(touched, orderFieldName)
                      setFieldValue(orderFieldName, orderId)
                    }
                  }}
                />
                <AddOrderPopupButton
                  id="form-add-order-button"
                  disabled={disabled}
                  isDspDeal={isDspDeal}
                  onSave={result => {
                    let newValues = setValueAndGetObject({ ...values }, orderFieldName, result.order.id)
                    if (!isDspDeal) {
                      newValues = setValueAndGetObject(newValues, advertiserFieldName, result.advertiser.id)
                    }
                    setValues(newValues)
                  }}
                  sxButton={{ ml: "1rem" }}
                />
              </FlexBox>
              {!isDspDeal && (
                <TextField
                  id="advertiser-name"
                  sx={{ width: "100%", mt: "1rem", ...sxSecondField }}
                  disabled
                  label={translate("label.advertiser")}
                  variant="outlined"
                  size="small"
                  value={advertiser ? advertiser.name : translate("order.choose")}
                />
              )}
            </FlexBox>
          )
        }}
      </Field>
    )
  }
)

export const FieldSelectOrder = connect(
  (state: IRootState): IFieldSelectOrderConnectProps => ({
    orders: state.order.orders,
    advertisers: state.advertiser.advertisers
  })
)(FieldSelectOrderFn)
